<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { getSortedAgentsByAvailability } from 'dashboard/helper/agentHelper';
import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  selectedCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['select', 'close']);

const { t } = useI18n();
const store = useStore();
const verifiedAgents = useMapGetter('agents/getVerifiedAgents');

onMounted(() => {
  store.dispatch('agents/get');
});

const query = ref('');
const selectedAgent = ref(null);
const goBackToAgentList = ref(false);

const noneAgent = {
  id: null,
  name: t('AGENT_MGMT.MULTI_SELECTOR.LIST.NONE'),
  confirmed: true,
  role: 'agent',
  account_id: 0,
};

const agentsList = computed(() =>
  getSortedAgentsByAvailability(verifiedAgents.value || [])
);

const filteredAgents = computed(() => {
  if (query.value) {
    return agentsList.value.filter(agent =>
      agent.name.toLowerCase().includes(query.value.toLowerCase())
    );
  }
  return [noneAgent, ...agentsList.value];
});

const assignAgent = agent => {
  selectedAgent.value = agent;
};

const goBack = () => {
  goBackToAgentList.value = true;
  selectedAgent.value = null;
};

const submit = () => {
  emit('select', selectedAgent.value);
};

const onClose = () => {
  emit('close');
};
</script>

<template>
  <div
    class="absolute ltr:right-0 rtl:left-0 top-9 mt-0.5 z-20 min-w-[15rem] w-auto max-w-[75%] bg-n-alpha-3 backdrop-blur-[100px] border border-solid border-n-weak rounded-lg shadow-md"
  >
    <div class="flex items-center justify-between p-2.5">
      <span class="text-sm font-medium">{{
        t('CONTACTS_BULK_ACTIONS.ASSIGN_AGENT.TITLE')
      }}</span>
      <Button ghost xs slate icon="i-lucide-x" @click="onClose" />
    </div>
    <div class="overflow-y-auto max-h-[15rem]">
      <ul v-if="!selectedAgent" class="m-0 list-none">
        <li
          class="py-0 px-2.5 sticky top-0 z-20 bg-n-alpha-3 backdrop-blur-[100px]"
        >
          <div
            class="flex items-center justify-between h-8 gap-2 py-0 px-2.5 bg-n-alpha-black2 border border-solid border-n-strong rounded-md"
          >
            <i class="i-lucide-search text-n-slate-10 size-4" />
            <input
              v-model="query"
              type="search"
              :placeholder="
                t('CONTACTS_BULK_ACTIONS.ASSIGN_AGENT.SEARCH_PLACEHOLDER')
              "
              class="border-0 text-xs m-0 bg-transparent dark:bg-transparent outline-none h-[unset] w-full"
            />
          </div>
        </li>
        <li
          v-for="agent in filteredAgents"
          :key="agent.id ?? 'none'"
          class="last:[&>.agent-list-item]:rounded-b-lg"
        >
          <button
            type="button"
            class="agent-list-item flex items-center w-full p-2.5 gap-2 cursor-pointer hover:bg-n-slate-3 dark:hover:bg-n-solid-3 bg-transparent border-0 text-left"
            @click="assignAgent(agent)"
          >
            <Avatar
              v-if="agent.id"
              :name="agent.name"
              :src="agent.thumbnail"
              :status="agent.availability_status"
              :size="22"
              hide-offline-status
              rounded-full
            />
            <span class="text-sm text-n-slate-12">{{ agent.name }}</span>
          </button>
        </li>
      </ul>
      <div v-else class="flex flex-col p-2.5">
        <p class="flex-grow text-sm">
          <template v-if="selectedAgent.id">
            {{
              t('CONTACTS_BULK_ACTIONS.ASSIGN_AGENT.CONFIRM_ASSIGN', {
                count: selectedCount,
                agentName: selectedAgent.name,
              })
            }}
          </template>
          <template v-else>
            {{
              t('CONTACTS_BULK_ACTIONS.ASSIGN_AGENT.CONFIRM_UNASSIGN', {
                count: selectedCount,
              })
            }}
          </template>
        </p>
        <div class="w-full grid grid-cols-2 gap-2.5">
          <Button
            faded
            sm
            slate
            type="reset"
            :label="t('CONTACTS_BULK_ACTIONS.ASSIGN_AGENT.GO_BACK')"
            @click="goBack"
          />
          <Button
            sm
            type="submit"
            :label="t('CONTACTS_BULK_ACTIONS.ASSIGN_AGENT.CONFIRM')"
            @click="submit"
          />
        </div>
      </div>
    </div>
  </div>
</template>
